export const HIGHLIGHT_COLORS = [
  "#C6EFCE",
  "#FFEB9C",
  "#FCE4D6",
  "#FFC7CE",
  "#DDEBF7",
  "#D9D9D9"
];

export const HIGHLIGHT_BASE_KINDS = [
  "callNumber",
  "ra",
  "name",
  "desistente",
  "absencesDiscipline",
  "absencesTotal",
  "absencesPercentDiscipline",
  "absencesPercentTotal",
  "gradeAny",
  "gradeAverage",
  "gradeByName"
];

export const HIGHLIGHT_APPLY_KINDS = [
  "callNumber",
  "ra",
  "name",
  "absencesDiscipline",
  "absencesTotal",
  "absencesPercentDiscipline",
  "absencesPercentTotal",
  "gradeAny",
  "gradeAverage"
];

export const HIGHLIGHT_OPERATORS = [
  "empty",
  "notEmpty",
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "between",
  "contains"
];

export const HIGHLIGHT_GRAY = "#D9D9D9";

const OPERATOR_SET = new Set(HIGHLIGHT_OPERATORS);
const BASE_KIND_SET = new Set(HIGHLIGHT_BASE_KINDS);
const APPLY_KIND_SET = new Set(HIGHLIGHT_APPLY_KINDS);
const COLOR_SET = new Set(HIGHLIGHT_COLORS);

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function createHighlightId() {
  return `hl_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

export function normalizeHeaderName(value) {
  return String(value == null ? "" : value)
    .trim()
    .toLowerCase();
}

function parseComparable(value) {
  if (value == null) return { empty: true, text: "", number: null };
  const raw = String(value).trim();
  if (!raw) return { empty: true, text: "", number: null };

  const numericRaw = raw
    .replace(/%/g, "")
    .replace(/\s/g, "")
    .replace(",", ".");
  const numeric = Number(numericRaw);
  if (Number.isFinite(numeric) && /^-?\d/.test(numericRaw)) {
    return { empty: false, text: raw, number: numeric };
  }
  if (/[a-zA-Z]/.test(raw)) {
    return { empty: false, text: raw, number: null };
  }
  return { empty: false, text: raw, number: null };
}

function mapLookup(map, key) {
  if (!map || key == null) return null;
  if (Object.prototype.hasOwnProperty.call(map, key)) return map[key];
  const matched = Object.keys(map).find(item => String(item) === String(key));
  return matched != null ? map[matched] : null;
}

export function matchesHighlightOperator(cellValue, operator, value, valueTo) {
  const parsed = parseComparable(cellValue);
  const target = parseComparable(value);
  const targetTo = parseComparable(valueTo);

  switch (operator) {
    case "empty":
      return parsed.empty;
    case "notEmpty":
      return !parsed.empty;
    case "contains":
      if (parsed.empty) return false;
      return parsed.text
        .toLowerCase()
        .includes(String(value == null ? "" : value).toLowerCase());
    case "eq":
      if (parsed.empty || target.empty) return parsed.empty && target.empty;
      if (parsed.number != null && target.number != null) {
        return parsed.number === target.number;
      }
      return parsed.text.toLowerCase() === target.text.toLowerCase();
    case "neq":
      return !matchesHighlightOperator(cellValue, "eq", value, valueTo);
    case "gt":
      return (
        parsed.number != null &&
        target.number != null &&
        parsed.number > target.number
      );
    case "gte":
      return (
        parsed.number != null &&
        target.number != null &&
        parsed.number >= target.number
      );
    case "lt":
      return (
        parsed.number != null &&
        target.number != null &&
        parsed.number < target.number
      );
    case "lte":
      return (
        parsed.number != null &&
        target.number != null &&
        parsed.number <= target.number
      );
    case "between":
      return (
        parsed.number != null &&
        target.number != null &&
        targetTo.number != null &&
        parsed.number >= Math.min(target.number, targetTo.number) &&
        parsed.number <= Math.max(target.number, targetTo.number)
      );
    default:
      return false;
  }
}

function operatorNeedsValue(operator) {
  return operator !== "empty" && operator !== "notEmpty";
}

function operatorNeedsValueTo(operator) {
  return operator === "between";
}

function normalizeBase(raw) {
  if (!isPlainObject(raw)) return null;
  const kind = String(raw.kind || "");
  if (!BASE_KIND_SET.has(kind)) return null;
  const base = { kind };
  if (kind === "gradeByName") {
    const name = String(raw.name == null ? "" : raw.name).trim();
    if (!name) return null;
    base.name = name;
  }
  return base;
}

export function applyKindFromBase(baseKind) {
  if (baseKind === "gradeByName") return "gradeAny";
  if (baseKind === "desistente") return "name";
  if (APPLY_KIND_SET.has(baseKind)) return baseKind;
  return "name";
}

export function isDesistenteKind(kind) {
  return kind === "desistente";
}

export function desistenteFlag(value) {
  return String(value == null ? "" : value)
    .trim()
    .toUpperCase() === "S"
    ? "S"
    : "N";
}

function normalizeApplyTo(raw, baseKind) {
  const fallback = [applyKindFromBase(baseKind)];
  if (!isPlainObject(raw) || raw.mode !== "columns") {
    return { mode: "row", columns: [] };
  }
  const columns = Array.isArray(raw.columns)
    ? raw.columns.filter(key => APPLY_KIND_SET.has(key))
    : [];
  if (!columns.length) {
    return { mode: "columns", columns: fallback };
  }
  return { mode: "columns", columns };
}

export function normalizeHighlight(raw, index = 0) {
  if (!isPlainObject(raw)) return null;
  const base = normalizeBase(raw.base);
  if (!base) return null;

  const isDesistente = isDesistenteKind(base.kind);
  const operator = isDesistente ? "eq" : String(raw.operator || "");
  if (!OPERATOR_SET.has(operator)) return null;
  const color = String(raw.color || "");
  if (!COLOR_SET.has(color)) return null;

  const highlight = {
    id: raw.id ? String(raw.id) : createHighlightId(),
    order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : index,
    base,
    operator,
    value: isDesistente ? "S" : "",
    valueTo: "",
    color,
    applyTo: normalizeApplyTo(raw.applyTo, base.kind)
  };

  if (!isDesistente && operatorNeedsValue(operator)) {
    highlight.value = raw.value == null ? "" : String(raw.value);
  }
  if (!isDesistente && operatorNeedsValueTo(operator)) {
    highlight.valueTo = raw.valueTo == null ? "" : String(raw.valueTo);
  }

  return highlight;
}

export function normalizeHighlights(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((item, index) => normalizeHighlight(item, index))
    .filter(Boolean)
    .sort((a, b) => a.order - b.order)
    .map((item, index) => ({ ...item, order: index }));
}

export function createEmptyHighlight(partial = {}) {
  const base = partial.base || { kind: "absencesPercentDiscipline" };
  return normalizeHighlight(
    {
      id: createHighlightId(),
      order: 0,
      operator: "gte",
      value: "",
      valueTo: "",
      color: HIGHLIGHT_COLORS[3],
      ...partial,
      base,
      applyTo: partial.applyTo || { mode: "row", columns: [] }
    },
    0
  );
}

function isGradeAverageColumn(column) {
  return column && String(column.CODPROVA) === "MED";
}

function gradeHeaderMatches(column, name) {
  if (!column || isGradeAverageColumn(column)) return false;
  return normalizeHeaderName(column.header) === normalizeHeaderName(name);
}

function absenceCount(cell) {
  return cell && cell.absences != null ? cell.absences : 0;
}

function absencePercent(cell) {
  return cell && cell.percent != null ? cell.percent : 0;
}

function periodEntries(ctx) {
  if (ctx.boardsByPeriod && typeof ctx.boardsByPeriod === "object") {
    const ids = Array.isArray(ctx.visiblePeriodIds)
      ? ctx.visiblePeriodIds
      : Object.keys(ctx.boardsByPeriod);
    return ids
      .map(id => {
        const board = mapLookup(ctx.boardsByPeriod, id);
        if (!board) return null;
        return { periodId: String(id), board };
      })
      .filter(Boolean);
  }

  return [
    {
      periodId: null,
      board: {
        absencesByRa: ctx.absencesByRa,
        gradesByRa: ctx.gradesByRa,
        gradeColumns: ctx.gradeColumns,
        disciplines: ctx.disciplines
      }
    }
  ];
}

function aggregateTotalCell(entries, ra) {
  let totalAbsences = 0;
  let totalAllowed = 0;
  entries.forEach(({ board }) => {
    const row = mapLookup(board.absencesByRa, ra);
    const summary = (row && row.total) || (row && row.period);
    if (summary) {
      totalAbsences += Number(summary.absences) || 0;
      totalAllowed += Number(summary.maxAbsences) || 0;
      return;
    }
    (board.disciplines || []).forEach(discipline => {
      const cell = mapLookup(row && row.disciplines, discipline.idTurmaDisc);
      totalAbsences += absenceCount(cell);
      totalAllowed += Number(discipline.maxAbsences) || 0;
    });
  });
  return {
    absences: totalAbsences,
    percent:
      totalAllowed > 0 ? Math.round((totalAbsences / totalAllowed) * 100) : 0
  };
}

function collectBaseMatches(rule, ctx) {
  const { student } = ctx;
  const ra = student && student.studentId;
  const { kind } = rule.base;
  const matches = [];
  const entries = periodEntries(ctx);

  const pushIf = (value, meta) => {
    if (
      matchesHighlightOperator(value, rule.operator, rule.value, rule.valueTo)
    ) {
      matches.push(meta);
    }
  };

  if (kind === "callNumber") {
    pushIf(student.callNumber, { kind });
    return matches;
  }
  if (kind === "ra") {
    pushIf(ra, { kind });
    return matches;
  }
  if (kind === "name") {
    pushIf(student.name, { kind });
    return matches;
  }
  if (kind === "desistente") {
    pushIf(desistenteFlag(student.desistente), { kind });
    return matches;
  }

  if (kind === "absencesTotal" || kind === "absencesPercentTotal") {
    const cell = aggregateTotalCell(entries, ra);
    const value =
      kind === "absencesTotal" ? absenceCount(cell) : absencePercent(cell);
    pushIf(value, { kind });
    return matches;
  }

  if (kind === "absencesDiscipline" || kind === "absencesPercentDiscipline") {
    entries.forEach(({ periodId, board }) => {
      const absenceRow = mapLookup(board.absencesByRa, ra);
      (board.disciplines || []).forEach(discipline => {
        const cell = mapLookup(
          absenceRow && absenceRow.disciplines,
          discipline.idTurmaDisc
        );
        const value =
          kind === "absencesDiscipline"
            ? absenceCount(cell)
            : absencePercent(cell);
        pushIf(value, {
          kind,
          periodId,
          idTurmaDisc: discipline.idTurmaDisc
        });
      });
    });
    return matches;
  }

  entries.forEach(({ periodId, board }) => {
    const gradeRow = mapLookup(board.gradesByRa, ra);
    (board.gradeColumns || []).forEach(column => {
      const isAverage = isGradeAverageColumn(column);
      if (kind === "gradeAverage" && !isAverage) return;
      if (kind === "gradeAny" && isAverage) return;
      if (
        kind === "gradeByName" &&
        !gradeHeaderMatches(column, rule.base.name)
      ) {
        return;
      }
      if (
        kind !== "gradeAny" &&
        kind !== "gradeAverage" &&
        kind !== "gradeByName"
      ) {
        return;
      }
      const value = gradeRow ? gradeRow[column.id] : "";
      pushIf(value, {
        kind,
        periodId,
        idTurmaDisc: column.idTurmaDisc,
        gradeColumnId: column.id,
        isAverage
      });
    });
  });

  return matches;
}

function cellApplyKind(cellMeta) {
  if (cellMeta.kind === "grade") {
    return cellMeta.isAverage ? "gradeAverage" : "gradeAny";
  }
  return cellMeta.kind;
}

function samePeriod(match, cellMeta) {
  if (match.periodId == null || cellMeta.periodId == null) return true;
  return String(match.periodId) === String(cellMeta.periodId);
}

function sameDiscipline(match, cellMeta) {
  if (match.idTurmaDisc == null || cellMeta.idTurmaDisc == null) return true;
  return String(match.idTurmaDisc) === String(cellMeta.idTurmaDisc);
}

function sameGradeColumn(match, cellMeta) {
  if (match.gradeColumnId == null || cellMeta.gradeColumnId == null) {
    return true;
  }
  return String(match.gradeColumnId) === String(cellMeta.gradeColumnId);
}

function isScopedKind(kind) {
  return (
    kind === "absencesDiscipline" ||
    kind === "absencesPercentDiscipline" ||
    kind === "gradeAny" ||
    kind === "gradeAverage" ||
    kind === "gradeByName"
  );
}

function isCellTarget(rule, cellMeta, matches) {
  if (!rule.applyTo || rule.applyTo.mode !== "columns") return true;

  const applyKind = cellApplyKind(cellMeta);
  if (!(rule.applyTo.columns || []).includes(applyKind)) return false;

  const baseKind = rule.base.kind;
  if (!isScopedKind(baseKind) || !isScopedKind(applyKind)) return true;

  const requireSameGradeColumn =
    applyKind === "gradeAny" &&
    (baseKind === "gradeAny" || baseKind === "gradeByName");

  return matches.some(match => {
    if (!samePeriod(match, cellMeta)) return false;
    if (!sameDiscipline(match, cellMeta)) return false;
    if (requireSameGradeColumn && !sameGradeColumn(match, cellMeta)) {
      return false;
    }
    return true;
  });
}

export function resolveCellHighlightColor(highlights, cellMeta, ctx) {
  if (!Array.isArray(highlights) || !highlights.length) return null;
  let color = null;
  const sorted = [...highlights].sort((a, b) => a.order - b.order);
  sorted.forEach(rule => {
    const matches = collectBaseMatches(rule, ctx);
    if (!matches.length) return;
    if (isCellTarget(rule, cellMeta, matches)) {
      color = rule.color;
    }
  });
  return color;
}

function probeCellMeta(applyKind) {
  if (applyKind === "gradeAny") {
    return { kind: "grade", isAverage: false };
  }
  if (applyKind === "gradeAverage") {
    return { kind: "grade", isAverage: true };
  }
  return { kind: applyKind };
}

function ruleAffectsAnyStudent(rule, students, ctxBase) {
  if (!rule || !rule.base) return false;

  let probeKinds = ["name"];
  if (rule.applyTo && rule.applyTo.mode === "columns") {
    const columns = rule.applyTo.columns || [];
    if (columns.length) {
      probeKinds = columns;
    }
  }

  return (students || []).some(student =>
    probeKinds.some(applyKind =>
      Boolean(
        resolveCellHighlightColor([rule], probeCellMeta(applyKind), {
          ...ctxBase,
          student
        })
      )
    )
  );
}

export function buildGeneralBoardHighlightLegend(
  highlights,
  students,
  ctxBase = {}
) {
  if (!Array.isArray(highlights) || !highlights.length) return [];
  if (!Array.isArray(students) || !students.length) return [];

  return [...highlights]
    .sort((a, b) => a.order - b.order)
    .filter(rule => ruleAffectsAnyStudent(rule, students, ctxBase))
    .map(rule => ({
      id: rule.id || `${rule.base.kind}-${rule.order}`,
      color: rule.color,
      kind: rule.base.kind,
      operator: rule.operator,
      value: rule.value,
      valueTo: rule.valueTo
    }));
}

export function operatorRequiresValue(operator) {
  return operatorNeedsValue(operator);
}

export function operatorRequiresValueTo(operator) {
  return operatorNeedsValueTo(operator);
}
